import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter.jsx';
import {
  selectCSVMiltonReport,
  selectEmployeeData,
  selectMiltonReport,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useForm } from 'react-hook-form';
import {
  Box,
  Paper,
  Grid,
  Button,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import {
  getCSVMiltonReportData,
  getEmployeeList,
  getMiltonReportData,
  setCSVMiltonReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton.jsx';
import SelectRebuild from '../../../components/Inputs/SelectRebuild.jsx';
import { GROUPBY } from '../../../constants/Reports';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import { useBaseData } from './hooks/useBaseData.jsx';
import { useHeaders } from './hooks/useHeaders.jsx';
import { Card } from './components/Card.jsx';
import { useOvertimeData } from './hooks/useOvertimeData.jsx';
import { saveCsvToFile } from '../../../utils/saveCsvToFile.js';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const defaultFormValues = {
  emp_code: { id: 'ALL', name: 'All' },
  groupby: { id: GROUPBY[0].value, name: GROUPBY[0].title },
};

const MiltonReport = () => {
  const dispatch = useDispatch();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { miltonReportData, isLoadingMiltonReport } =
    useSelector(selectMiltonReport);
  const { miltonCSVReportData, isLoadingCSVMiltonReport } = useSelector(
    selectCSVMiltonReport
  );
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const { fromDate, toDate, render } = useDateRangeFilter();

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });
  const { emp_code: employees, groupby } = watch();

  const outOfGradeHeaders = useHeaders('base', groupby.id);
  const townHeaders = useHeaders('base', groupby.id);
  const privateHeaders = useHeaders('base', groupby.id);
  const ptoHeaders = useHeaders('base', groupby.id);
  const overtimeHeaders = useHeaders('overtime', groupby.id);

  const outOfGradeData = useBaseData(
    miltonReportData.oog,
    groupby,
    outOfGradeHeaders
  );
  const townData = useBaseData(miltonReportData.town, groupby, townHeaders);
  const privateData = useBaseData(
    miltonReportData.private,
    groupby,
    privateHeaders
  );
  const ptoData = useBaseData(miltonReportData.pto, groupby, ptoHeaders);
  const overtimeData = useOvertimeData(
    miltonReportData.overtime,
    groupby,
    overtimeHeaders
  );

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp: employees.id !== 'ALL' ? [employees.id] : null,
    };
  }, [fromDate, toDate, employees, groupby.id]);

  const handleFindClick = () => dispatch(getMiltonReportData(param));
  const handleDownloadReport = () =>
    dispatch(getCSVMiltonReportData({ ...param, csv: 1 }));

  useEffect(() => {
    if (miltonCSVReportData?.data) {
      saveCsvToFile(miltonCSVReportData.data, miltonCSVReportData.filename);
      dispatch(setCSVMiltonReportData({ data: null, filename: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miltonCSVReportData]);

  useEffect(
    () => {
      dispatch(getEmployeeList());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      dispatch(getMiltonReportData(param));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groupby]
  );

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple={false}
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
          enableAllOption={true}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={isLoadingCSVMiltonReport}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);

  const tableData = [
    {
      title: 'Personal Time Off',
      data: ptoData,
      columns: ptoHeaders,
    },
    {
      title: 'Overtime',
      data: overtimeData,
      columns: overtimeHeaders,
    },
    {
      title: 'Out Of Grade',
      data: outOfGradeData,
      columns: outOfGradeHeaders,
    },
    {
      title: 'Town Details',
      data: townData,
      columns: townHeaders,
    },
    {
      title: 'Private Details',
      data: privateData,
      columns: privateHeaders,
    },
  ];
  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Milton Report Details" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig}
            columns={isTabletVerticalWidth ? 1 : rawFilterConfig.length}
          />
        </Grid>

        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                mt: 'auto',
              }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>

        {isLoadingMiltonReport ? (
          <Box
            sx={{ minHeight: '100px' }}
            className="w-100 h-100 d-flex justify-content-center align-items-center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {tableData.map((item) =>
              item.data.length > 0 ? <Card {...item} /> : null
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default MiltonReport;
